import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/lib/i18n";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvitation from "@/pages/AcceptInvitation";
import { Terms, Privacy } from "@/pages/Legal";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import ApiKeys from "@/pages/ApiKeys";
import AuditLogs from "@/pages/AuditLogs";
import Integrations from "@/pages/Integrations";
import Billing from "@/pages/Billing";
import Notifications from "@/pages/Notifications";
import CompanySettings from "@/pages/settings/CompanySettings";
import ProfileSettings from "@/pages/settings/ProfileSettings";
import SecuritySettings from "@/pages/settings/SecuritySettings";
import SuperAdmin from "@/pages/SuperAdmin";
import WhatsApp from "@/pages/WhatsApp";
import QrGatewayConnect from "@/pages/QrGatewayConnect";
import QuickSend from "@/pages/QuickSend";
import Templates from "@/pages/Templates";
import {
  Conversations, Contacts, Lists, Campaigns,
  Automations, CalendarPage, Statistics,
} from "@/pages/Placeholders";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-center" richColors />
            <Routes>
              {/* Public */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* Onboarding */}
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

              {/* Super Admin console */}
              <Route path="/admin/*" element={<ProtectedRoute role="super_admin"><SuperAdmin /></ProtectedRoute>} />

              {/* App shell (tenant) */}
              <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/conversations" element={<Conversations />} />
                <Route path="/quick-send" element={<ProtectedRoute permission="whatsapp.messages.send"><QuickSend /></ProtectedRoute>} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/lists" element={<Lists />} />
                <Route path="/campaigns" element={<Campaigns />} />
                <Route path="/templates" element={<ProtectedRoute permission="whatsapp.templates.read"><Templates /></ProtectedRoute>} />
                <Route path="/automations" element={<Automations />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/statistics" element={<Statistics />} />
                <Route path="/whatsapp" element={<ProtectedRoute permission="whatsapp.channels.read"><WhatsApp /></ProtectedRoute>} />
                <Route path="/qr-gateway" element={<ProtectedRoute permission="whatsapp.channels.read"><QrGatewayConnect /></ProtectedRoute>} />
                <Route path="/integrations" element={<ProtectedRoute permission="integrations.view"><Integrations /></ProtectedRoute>} />
                <Route path="/keys" element={<ProtectedRoute permission="apikeys.view"><ApiKeys /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute permission="users.view"><Users /></ProtectedRoute>} />
                <Route path="/audit-logs" element={<ProtectedRoute permission="audit.view"><AuditLogs /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute permission="billing.view"><Billing /></ProtectedRoute>} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<ProtectedRoute permission="settings.view"><CompanySettings /></ProtectedRoute>} />
                <Route path="/settings/profile" element={<ProfileSettings />} />
                <Route path="/settings/security" element={<SecuritySettings />} />
              </Route>

              <Route path="/home" element={<RootRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
